import type {
  AvailableRanges,
  CalendarEvent,
  Hour,
  ISODate,
} from '../../shared/ui/components/EventCalendar/types';
import type { AvailabilityScheduleKind } from '../calendar-settings/availability/availabilityTransforms';
import type {
  AvailabilitySettingsResponse,
  AvailabilityWindow,
} from '../calendar-settings/availability/availabilityTypes';
import type {
  AdminCalendarViewResponse,
  CalendarViewAccount,
  CalendarViewAppointment,
  CalendarViewBlock,
  CalendarViewSession,
  CalendarViewWarning,
} from './types';

export interface CalendarViewModel {
  events: CalendarEvent[];
  hours: number[];
  availableRanges: AvailableRanges;
  chemistryAvailableRanges: AvailableRanges;
  warnings: CalendarViewWarning[];
}

interface ZonedInstantParts {
  date: ISODate;
  hour: Hour;
  offset: number;
}

interface CalendarIntervalSegment {
  date: ISODate;
  start: Hour;
  end: Hour;
  suffix: string;
}

interface OffsetTransition {
  epoch: number;
  before: ZonedInstantParts;
  after: ZonedInstantParts;
}

const SESSION_TYPE_LABELS: Record<CalendarViewSession['session_type'], string> = {
  chem_call: 'Chemistry',
  coaching_session: 'Coaching session',
  unconfirmed: 'Unconfirmed',
};

export function toAdminCalendarViewModel(response: AdminCalendarViewResponse): CalendarViewModel {
  return {
    events: toCalendarEvents(response),
    // Show the full 24-hour day so events outside the coach's availability are
    // visible. Availability is conveyed via availableRanges (background bands),
    // and WeekView auto-scrolls to 8am on mount so mornings lead by default.
    hours: range(0, 23),
    availableRanges: toAvailableRanges(response.availability),
    chemistryAvailableRanges: toAvailableRanges(response.availability, 'chemistry'),
    warnings: [...response.warnings],
  };
}

export function toCalendarEvents(response: AdminCalendarViewResponse): CalendarEvent[] {
  const calendarSummaries = calendarSummaryLookup(response.accounts);
  const offsetTransitions = calendarOffsetTransitions(response);
  return [
    ...(response.sessions ?? []).flatMap((session) =>
      toCalendarEventsForSession(session, response.timezone, offsetTransitions)
    ),
    ...(response.appointments ?? []).flatMap((appointment) =>
      toCalendarEventsForAppointment(appointment, response.timezone, offsetTransitions)
    ),
    ...response.blocks.flatMap((block) =>
      toCalendarEventsForBlock(block, response.timezone, calendarSummaries, offsetTransitions)
    ),
  ];
}

export function toAvailableRanges(
  availability: AvailabilitySettingsResponse,
  kind: AvailabilityScheduleKind = 'coaching'
): AvailableRanges {
  const result: AvailableRanges = {};
  // Older servers omit chemistry_days; fall back to the Coaching hours.
  const days =
    kind === 'chemistry' ? (availability.chemistry_days ?? availability.days) : availability.days;
  for (const day of days) {
    if (!day.enabled || day.windows.length === 0) continue;
    // AvailabilityWeekday: 1=Mon..6=Sat, 7=Sun → JS DoW: 0=Sun..6=Sat
    const jsDow = day.weekday === 7 ? 0 : day.weekday;
    result[jsDow] = day.windows
      .map((w) => ({ start: timeToHour(w.start_time), end: timeToHour(w.end_time) }))
      .filter((range) => range.start < range.end);
  }
  return result;
}

export function isoInstantToLocalDate(instant: string, timezone: string): ISODate {
  return zonedInstantParts(instant, timezone).date;
}

export function isoInstantToLocalHour(instant: string, timezone: string): Hour {
  return zonedInstantParts(instant, timezone).hour;
}

function toCalendarEventsForBlock(
  block: CalendarViewBlock,
  timezone: string,
  calendarSummaries: Map<string, string>,
  offsetTransitions: OffsetTransition[]
): CalendarEvent[] {
  return splitCalendarInterval(block.start, block.end, timezone, offsetTransitions).map(
    (segment) => {
      const calendarSummary = calendarSummaryForBlock(block, calendarSummaries);
      return {
        id: `${block.id}${segment.suffix}`,
        kind: 'block' as const,
        date: segment.date,
        start: segment.start,
        end: segment.end,
        title: externalBlockTitle(calendarSummary),
        meta: {
          provider: block.provider,
          connectionId: block.connection_id,
          providerCalendarId: block.provider_calendar_id,
          calendarSummary,
          sourceBlockId: block.id,
        },
      };
    }
  );
}

function toCalendarEventsForSession(
  session: CalendarViewSession,
  timezone: string,
  offsetTransitions: OffsetTransition[]
): CalendarEvent[] {
  const member = session.participants.find((participant) => participant.role === 'coachee');
  const isChemistryCall = session.session_type === 'chem_call';
  const activeTitle = isChemistryCall
    ? 'Chemistry Call'
    : member?.display_name?.trim() || undefined;
  const title =
    session.status === 'cancelled' ? `Cancelled · ${activeTitle ?? 'Session'}` : activeTitle;
  const segments = session.end
    ? splitCalendarInterval(session.start, session.end, timezone, offsetTransitions)
    : [pointCalendarInterval(session.start, timezone)];
  return segments.map((segment) => ({
    id: `session:${session.session_id}${segment.suffix}`,
    kind: 'session' as const,
    date: segment.date,
    start: segment.start,
    end: segment.end,
    memberId: member?.person_id,
    title,
    meta: {
      sessionId: session.session_id,
      occurrenceId: session.occurrence_id,
      status: session.status,
      sessionType: SESSION_TYPE_LABELS[session.session_type],
      canonicalSessionType: session.session_type,
      participants: session.participants,
      actionReference: session.action_reference,
    },
  }));
}

function toCalendarEventsForAppointment(
  appointment: CalendarViewAppointment,
  timezone: string,
  offsetTransitions: OffsetTransition[]
): CalendarEvent[] {
  const member = appointment.expected_identities.member;
  const titlePrefix = appointment.booking_status === 'pending' ? 'Pending · ' : '';
  const issueCodes = appointment.issues.map((issue) => issue.code);
  const segments = splitCalendarInterval(
    appointment.current_schedule.start_at,
    appointment.current_schedule.end_at,
    timezone,
    offsetTransitions
  );
  return segments.map((segment) => ({
    id: `appointment:${appointment.id}${segment.suffix}`,
    kind: 'session' as const,
    date: segment.date,
    start: segment.start,
    end: segment.end,
    memberId: member.person_id,
    title: `${titlePrefix}${member.display_name}`,
    meta: {
      appointmentId: appointment.id,
      status: appointment.lifecycle_status,
      bookingStatus: appointment.booking_status,
      sessionType: appointment.kind === 'chemistry' ? 'Chemistry' : 'Coaching session',
      canonicalSessionType: appointment.kind === 'chemistry' ? 'chem_call' : 'coaching_session',
      actionReference: appointment.id,
      issueCodes,
    },
  }));
}

function pointCalendarInterval(instant: string, timezone: string): CalendarIntervalSegment {
  const start = zonedInstantParts(instant, timezone);
  return { date: start.date, start: start.hour, end: start.hour, suffix: '' };
}

function splitCalendarInterval(
  startInstant: string,
  endInstant: string,
  timezone: string,
  offsetTransitions: OffsetTransition[]
): CalendarIntervalSegment[] {
  const start = zonedInstantParts(startInstant, timezone);
  const end = zonedInstantParts(endInstant, timezone);
  const startEpoch = Date.parse(startInstant);
  const endEpoch = Date.parse(endInstant);
  const transitions = offsetTransitions.filter(
    ({ epoch }) => epoch > startEpoch && epoch <= endEpoch
  );
  const skippedDates = skippedLocalDates(transitions);
  const dates = datesBetweenInclusive(start.date, end.date).filter(
    (date) => !skippedDates.has(date)
  );
  return dates.flatMap((date) => {
    const segmentStart = date === start.date ? start.hour : 0;
    const wallClockEnd = date === end.date ? end.hour : 24;
    const suffix = dates.length > 1 ? `:${date}` : '';
    const transition = transitions.find(
      ({ before, after }) => date === before.date || date === after.date
    );
    if (transition && (date === transition.before.date || date === transition.after.date)) {
      const offsetAdjustment = transition.before.offset - transition.after.offset;
      const beforeBoundary = Math.round((transition.before.hour + 1 / 3600) * 60) / 60;
      const afterHour = Math.round(transition.after.hour * 60) / 60;
      const transitionSegments: CalendarIntervalSegment[] = [];
      if (date === transition.before.date) {
        transitionSegments.push({ date, start: segmentStart, end: beforeBoundary, suffix });
      }
      if (date === transition.after.date) {
        transitionSegments.push({
          date,
          start: afterHour,
          end: wallClockEnd,
          suffix: `${suffix}:${offsetAdjustment > 0 ? 'fold' : 'after-transition'}`,
        });
      }
      return transitionSegments.filter((segment) => segment.start < segment.end);
    }
    if (segmentStart >= wallClockEnd) return [];
    return [
      {
        date,
        start: segmentStart,
        end: wallClockEnd,
        suffix,
      },
    ];
  });
}

function skippedLocalDates(transitions: OffsetTransition[]): Set<ISODate> {
  const skipped = new Set<ISODate>();
  for (const { before, after } of transitions) {
    for (let date = addDays(before.date, 1); date < after.date; date = addDays(date, 1)) {
      skipped.add(date);
    }
  }
  return skipped;
}

function calendarOffsetTransitions(response: AdminCalendarViewResponse): OffsetTransition[] {
  const intervalInstants = [
    ...(response.sessions ?? []).flatMap((session) =>
      session.end ? [session.start, session.end] : []
    ),
    ...(response.appointments ?? []).flatMap((appointment) => [
      appointment.current_schedule.start_at,
      appointment.current_schedule.end_at,
    ]),
    ...response.blocks.flatMap((block) => [block.start, block.end]),
  ];
  if (intervalInstants.length === 0) return [];

  const epochs = intervalInstants.map(Date.parse);
  const startEpoch = Math.min(...epochs);
  const endEpoch = Math.max(...epochs);
  return timezoneOffsetTransitions(
    new Date(startEpoch).toISOString(),
    new Date(endEpoch).toISOString(),
    response.timezone
  );
}

function calendarSummaryLookup(accounts: CalendarViewAccount[]): Map<string, string> {
  const entries = accounts.flatMap((account) =>
    account.calendars.map(
      (calendar) =>
        [
          calendarLookupKey(account.connection_id, calendar.provider_calendar_id),
          calendar.summary.trim(),
        ] as const
    )
  );
  return new Map(entries.filter(([, summary]) => summary.length > 0));
}

function calendarSummaryForBlock(
  block: CalendarViewBlock,
  calendarSummaries: Map<string, string>
): string {
  const directSummary = block.calendar_summary?.trim();
  if (directSummary) {
    return directSummary;
  }

  const accountCalendarSummary = calendarSummaries.get(
    calendarLookupKey(block.connection_id, block.provider_calendar_id)
  );
  if (accountCalendarSummary) {
    return accountCalendarSummary;
  }

  return block.provider_calendar_id.trim();
}

function calendarLookupKey(connectionId: string, providerCalendarId: string): string {
  return `${connectionId.trim()}:${providerCalendarId.trim()}`;
}

function externalBlockTitle(calendarSummary: string): string {
  const trimmed = calendarSummary.trim();
  return trimmed ? `External block · ${trimmed}` : 'External block';
}

function zonedInstantParts(instant: string, timezone: string): ZonedInstantParts {
  const date = new Date(instant);
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const parts = Object.fromEntries(
    formatter.formatToParts(date).map((part) => [part.type, part.value])
  );
  const hour = Number(parts.hour) + Number(parts.minute) / 60 + Number(parts.second) / 3600;
  const localEpoch = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );
  const instantEpoch = Math.trunc(date.getTime() / 1000) * 1000;
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    hour,
    offset: (localEpoch - instantEpoch) / (60 * 60 * 1000),
  };
}

export function calendarLocalTimeToInstant(date: ISODate, hour: Hour, timezone: string): string {
  const [year, month, day] = date.split('-').map(Number);
  const wholeHour = Math.floor(hour);
  const minute = Math.round((hour - wholeHour) * 60);
  const localEpoch = Date.UTC(year, month - 1, day, wholeHour, minute);
  let instantEpoch = localEpoch;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const offset = zonedInstantParts(new Date(instantEpoch).toISOString(), timezone).offset;
    instantEpoch = localEpoch - offset * 60 * 60 * 1000;
  }
  const instant = new Date(instantEpoch).toISOString();
  const resolved = zonedInstantParts(instant, timezone);
  if (resolved.date !== date || Math.abs(resolved.hour - hour) > 1 / 3600) {
    throw new Error('local calendar time does not exist in timezone');
  }
  return instant;
}

function timezoneOffsetTransitions(
  startInstant: string,
  endInstant: string,
  timezone: string
): OffsetTransition[] {
  const transitions: OffsetTransition[] = [];
  const endEpoch = Date.parse(endInstant);
  let windowStart = Date.parse(startInstant);
  let windowStartOffset = zonedInstantParts(startInstant, timezone).offset;

  while (windowStart < endEpoch) {
    const windowEnd = Math.min(windowStart + 12 * 60 * 60 * 1000, endEpoch);
    const windowEndOffset = zonedInstantParts(new Date(windowEnd).toISOString(), timezone).offset;
    if (windowEndOffset !== windowStartOffset) {
      transitions.push(
        timezoneOffsetTransition(windowStart, windowEnd, timezone, windowStartOffset)
      );
    }
    windowStart = windowEnd;
    windowStartOffset = windowEndOffset;
  }

  return transitions;
}

function timezoneOffsetTransition(
  startEpoch: number,
  endEpoch: number,
  timezone: string,
  startOffset: number
): OffsetTransition {
  let beforeTransitionSecond = Math.floor(startEpoch / 1000);
  let afterTransitionSecond = Math.ceil(endEpoch / 1000);

  while (afterTransitionSecond - beforeTransitionSecond > 1) {
    const midpointSecond = Math.floor((beforeTransitionSecond + afterTransitionSecond) / 2);
    const midpointOffset = zonedInstantParts(
      new Date(midpointSecond * 1000).toISOString(),
      timezone
    ).offset;
    if (midpointOffset === startOffset) beforeTransitionSecond = midpointSecond;
    else afterTransitionSecond = midpointSecond;
  }

  const transitionEpoch = afterTransitionSecond * 1000;
  return {
    epoch: transitionEpoch,
    before: zonedInstantParts(new Date(transitionEpoch - 1000).toISOString(), timezone),
    after: zonedInstantParts(new Date(transitionEpoch).toISOString(), timezone),
  };
}

function datesBetweenInclusive(start: ISODate, end: ISODate): ISODate[] {
  const dates: ISODate[] = [];
  let cursor = start;
  while (cursor <= end) {
    dates.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return dates;
}

function addDays(date: ISODate, days: number): ISODate {
  const [year, month, day] = date.split('-').map(Number);
  const value = new Date(Date.UTC(year, month - 1, day + days));
  return value.toISOString().slice(0, 10);
}

function timeToHour(value: AvailabilityWindow['start_time']): Hour {
  const [hours, minutes] = value.split(':').map(Number);
  return hours + minutes / 60;
}

function range(start: number, end: number): number[] {
  return Array.from({ length: Math.max(0, end - start + 1) }, (_, index) => start + index);
}
